<template>
  <div v-if="hasUserProfiles">
    <q-table
      v-if="hasUserProfiles && disabledUsers.length > 0"
      title="Unapproved Users"
      :data="disabledUsers"
      :columns="columns"
      :loading="users.length === 0"
      :filter="unapprovedUsersTableFilter"
      row-key="email"
      :rows-per-page-options="[50, 150, 250, 500, 1000, 0]"
      flat
      bordered
    >
      <template v-slot:top-right>
        <q-input
          clearable
          dense
          outlined
          debounce="300"
          color="primary"
          v-model="unapprovedUsersTableFilter"
          placeholder="Filter..."
        >
          <template v-slot:append>
            <q-icon name="search" />
          </template>
        </q-input>
      </template>
      <template v-slot:body-cell-disabled="props">
        <q-td :props="props">
          <div>
            <q-btn-group>
              <q-btn
                color="positive"
                text-color="white"
                label="Enable"
                size="sm"
                @click="enableUser(props.row.uid, props.row.email)"
              />
              <q-btn
                color="negative"
                text-color="white"
                label="Delete"
                size="sm"
                @click="removeUser(props.row.uid)"
              />
            </q-btn-group>
          </div>
        </q-td>
      </template>
    </q-table>
    <br />
    <q-table
      title="Users"
      row-key="email"
      :data="enabledUsers"
      :columns="columns"
      :loading="loading"
      :filter="usersTableFilter"
      :pagination="initialPagination"
      :rows-per-page-options="[25, 50, 150, 250, 500, 1000, 0]"
      flat
      bordered
      binary-state-sort
    >
      <template v-slot:top-right>
        <div class="row items-center q-gutter-sm">
          <q-input
            clearable
            dense
            outlined
            debounce="300"
            color="primary"
            v-model="usersTableFilter"
            placeholder="Filter..."
          >
            <template v-slot:append>
              <q-icon name="search" />
            </template>
          </q-input>
          <q-btn
            color="primary"
            icon-right="archive"
            label="Export"
            no-caps
            @click="exportTable"
          />
        </div>
      </template>
      <template v-slot:body-cell-disabled="props">
        <q-td :props="props">
          <div>
            <q-btn-group>
              <q-btn
                color="warning"
                text-color="black"
                label="Disable"
                size="sm"
                @click="disableUser(props.row.uid)"
              />
              <q-btn
                color="negative"
                text-color="white"
                label="Delete"
                size="sm"
                @click="removeUser(props.row.uid)"
              />
            </q-btn-group>
          </div>
        </q-td>
      </template>
    </q-table>
  </div>
</template>
<script>
import { exportFile } from "quasar";
import { mapGetters, mapState } from "vuex";
import { getUsers, updateUser, deleteUser } from "@/apis/user-service";
import { getAllUserCourseReviewAggs } from "@/apis/course-rating-service";
// import { sendUserEnabledMessage } from "@/apis/email-service";
import { sortDate } from "@/utils";

const dateFormatOptions = {
  year: "numeric",
  month: "numeric",
  day: "numeric",
};
const dateFormatter = new Intl.DateTimeFormat("en-US", dateFormatOptions);

function wrapCsvValue(val, formatFn) {
  let formatted = formatFn !== void 0 ? formatFn(val) : val;

  formatted =
    formatted === void 0 || formatted === null ? "" : String(formatted);

  formatted = formatted.split('"').join('""');
  /**
   * Excel accepts \n and \r in strings, but some other CSV parsers do not
   * Uncomment the next two lines to escape new lines
   */
  // .split('\n').join('\\n')
  // .split('\r').join('\\r')

  return `"${formatted}"`;
}

export default {
  name: "Users",
  data() {
    return {
      loading: false,
      initialPagination: {
        sortBy: "created",
        descending: true,
        rowsPerPage: 25,
      },
      columns: [
        {
          name: "disabled",
          required: true,
          label: "Status",
          align: "left",
          field: row => !row.disabled,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "email",
          required: true,
          label: "Identifier",
          align: "left",
          field: row => row.email,
          // format: val => `${val}`,
          sortable: true,
        },
        {
          name: "displayName",
          required: true,
          label: "Display Name",
          align: "left",
          field: row => row.profile && row.profile.displayName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "firstName",
          required: true,
          label: "First Name",
          align: "left",
          field: row => row.profile && row.profile.firstName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "lastName",
          required: true,
          label: "Last Name",
          align: "left",
          field: row => row.profile && row.profile.lastName,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "created",
          required: true,
          label: "Created",
          align: "left",
          field: row => row.metadata.creationTime,
          format: val => {
            const date = new Date(val);
            const formattedDate = dateFormatter.format(date);

            return formattedDate;
          },
          sortable: true,
          sort: sortDate,
        },
        {
          name: "lastSignIn",
          required: true,
          label: "Last Sign In",
          align: "left",
          field: row => row.metadata.lastSignInTime,
          format: val => {
            const date = new Date(val);
            const formattedDate = dateFormatter.format(date);

            return formattedDate;
          },
          sortable: true,
          sort: sortDate,
        },
        {
          name: "loginCount",
          required: true,
          label: "Logins Since Creation",
          align: "left",
          field: row => {
            let loginCount = 0;

            if (row && row.profile) {
              loginCount = row.profile.loginCount;
            }

            return loginCount;
          },
          sortable: true,
        },
        {
          name: "wishlistCount",
          required: true,
          label: "Wishlist Items",
          align: "left",
          field: row => {
            let wishlistCount = 0;

            if (row && row.profile) {
              wishlistCount = row.profile.wishlistCount;
            }

            return wishlistCount;
          },
          sortable: true,
        },
        {
          name: "coursesPlayedCount",
          required: true,
          label: "Courses Played",
          align: "left",
          field: row => {
            let coursesPlayedCount = 0;

            if (row && row.profile) {
              coursesPlayedCount = row.profile.coursesPlayedCount;
            }

            return coursesPlayedCount;
          },
          sortable: true,
        },
        {
          name: "courseRatingsCount",
          required: true,
          label: "Courses Rated",
          align: "left",
          field: row => row.courseRatingsCount,
          sortable: true,
        },
        {
          name: "zipCode",
          required: true,
          label: "Zip Code",
          align: "left",
          field: row => row.profile && row.profile.zipCode,
          format: val => (val ? val : "-"),
          sortable: true,
        },
        {
          name: "receiveEmails",
          required: true,
          label: "Receive Emails?",
          align: "left",
          field: row => row.profile && row.profile.receiveEmails,
          format: val => {
            if (val === true) {
              return "Yes";
            } else if (val === false) {
              return "No";
            } else {
              return "-";
            }
          },
          sortable: true,
        },
        {
          name: "referralSource",
          required: true,
          label: "Referral Source(s)",
          align: "left",
          field: row => row.profile && row.profile.referralSource,
          format: val => (val ? val : "-"),
          sortable: true,
        },
      ],
      selectedUser: {},
      users: [],
      unapprovedUsersTableFilter: "",
      usersTableFilter: "",
    };
  },
  computed: {
    ...mapGetters(["uid"]),
    ...mapState(["idToken", "userProfiles"]),
    hasUserProfiles() {
      return Object.keys(this.userProfiles).length > 0;
    },
    enabledUsers() {
      return this.users.filter(user => !user.disabled);
    },
    disabledUsers() {
      return this.users.filter(user => user.disabled);
    },
  },
  beforeCreate() {
    this.$store.dispatch("fetchUserProfiles");
  },
  methods: {
    refreshUsers() {
      this.loading = true;

      const promises = [
        getUsers(this.idToken),
        getAllUserCourseReviewAggs(this.idToken),
      ];

      return Promise.all(promises)
        .then(([users, userCourseReviewAggs]) => {
          this.users = users.map(user => {
            const uid = user.uid;
            const profile = this.getUserProfileById(uid);
            const courseReviewAggs = userCourseReviewAggs.find(
              row => row.uid === uid,
            );
            let courseRatingsCount = 0;

            if (courseReviewAggs) {
              courseRatingsCount = Number(courseReviewAggs.ratingsCount);
            }

            return {
              ...user,
              profile,
              courseRatingsCount,
            };
          });
        })
        .catch(console.error)
        .finally(() => {
          this.loading = false;
        });
    },
    enableUser(uid, email) {
      const payload = {
        disabled: false,
      };

      this.$store.commit("setIsLoading", true);

      // TODO: we should get rid of Enable message now, right?
      return (
        updateUser(this.idToken, uid, payload)
          // .then(sendUserEnabledMessage(email))
          .then(() => {
            console.debug("enable User", uid);
          })
          .then(this.refreshUsers)
          .catch(console.error)
          .finally(() => {
            this.$store.commit("setIsLoading", false);
          })
      );
    },
    disableUser(uid) {
      const payload = {
        disabled: true,
      };

      this.$store.commit("setIsLoading", true);

      return updateUser(this.idToken, uid, payload)
        .then(() => {
          console.debug("disable User", uid);
        })
        .then(this.refreshUsers)
        .catch(console.error)
        .finally(() => {
          this.$store.commit("setIsLoading", false);
        });
    },
    removeUser(uid) {
      const proceed = window.confirm(
        "Are you sure you want to permanently delete this user?",
      );

      if (!proceed) {
        return;
      }

      this.$store.commit("setIsLoading", false);

      return deleteUser(this.idToken, uid)
        .then(() => {
          console.debug("removeUser", uid);
        })
        .then(this.refreshUsers)
        .catch(console.error)
        .finally(() => {
          this.$store.commit("setIsLoading", false);
        });
    },
    getUserProfileById(uid) {
      return this.userProfiles[uid];
    },
    exportTable() {
      console.debug("exportTable");

      const ignoreColumns = ["disabled"];
      const columns = this.columns.filter(
        col => !ignoreColumns.includes(col.name),
      );
      const data = this.enabledUsers;

      // naive encoding to csv format
      // from https://v1.quasar.dev/vue-components/table#exporting-data
      const content = [columns.map(col => wrapCsvValue(col.label))]
        .concat(
          data.map(row =>
            columns
              .filter(col => !ignoreColumns.includes(col.name))
              .map(col =>
                wrapCsvValue(
                  typeof col.field === "function"
                    ? col.field(row)
                    : row[col.field === void 0 ? col.name : col.field],
                  col.format,
                ),
              )
              .join(","),
          ),
        )
        .join("\r\n");

      const status = exportFile(
        "enabled-users-export.csv",
        content,
        "text/csv",
      );

      if (status !== true) {
        this.$q.notify({
          message: "Browser denied file download...",
          color: "negative",
          icon: "warning",
        });
      }
    },
  },
  watch: {
    userProfiles() {
      this.refreshUsers();
    },
  },
};
</script>
<style lang="scss" scoped></style>
